/**
 * Unified Fulfilment workspace paths and address projection.
 * Navigation/UX only — does not change inventory, order, or shipment writes.
 */

import { formatAddress } from "@/lib/shipping";
import { isUsableCanonicalAddress } from "@/lib/providers/order-addresses";

export const FULFILMENT_TABS = ["picking", "packing", "dispatch", "shipments"] as const;
export type FulfilmentTab = (typeof FULFILMENT_TABS)[number];

export const FULFILMENT_ROOT = "/fulfilment";

export function parseFulfilmentTab(raw: string | null | undefined): FulfilmentTab {
  if (raw === "packing" || raw === "dispatch" || raw === "shipments") return raw;
  if (raw === "shipping") return "dispatch";
  return "picking";
}

export function fulfilmentWorkspacePath(tab: FulfilmentTab = "picking"): string {
  return `${FULFILMENT_ROOT}?tab=${tab}`;
}

export function fulfilmentPickPath(taskId: string): string {
  return `${FULFILMENT_ROOT}/pick/${taskId}`;
}

export function fulfilmentPackingPath(fulfilmentId: string): string {
  return `${FULFILMENT_ROOT}/packing/${fulfilmentId}`;
}

export function fulfilmentLookupPath(): string {
  return `${FULFILMENT_ROOT}/lookup`;
}

export function fulfilmentShipmentPath(shipmentId: string): string {
  return `${FULFILMENT_ROOT}/shipments/${shipmentId}`;
}

export function legacyWarehouseRedirect(pathname: string, id?: string): string {
  if (pathname.startsWith("/warehouse/pick/") && id) return fulfilmentPickPath(id);
  if (pathname.startsWith("/warehouse/packing/") && id) return fulfilmentPackingPath(id);
  if (pathname === "/warehouse/lookup" || pathname.startsWith("/warehouse/lookup")) return fulfilmentLookupPath();
  return fulfilmentWorkspacePath("picking");
}

export function legacyShipmentsRedirect(pathname: string, id?: string): string {
  if (pathname.startsWith("/shipments/") && id) return fulfilmentShipmentPath(id);
  return fulfilmentWorkspacePath("shipments");
}

/** Order delivery address only. Never shipment.ship_to, sender, or customer master. */
export function resolveOrderShipTo(
  orderShipping: Record<string, unknown> | null | undefined,
  _customerShipping?: Record<string, unknown> | null,
): Record<string, unknown> | null {
  void _customerShipping;
  if (isUsableCanonicalAddress(orderShipping)) return orderShipping as Record<string, unknown>;
  return null;
}

export function formatShipTo(orderShipping: Record<string, unknown> | null | undefined): string {
  return formatAddress(resolveOrderShipTo(orderShipping, null));
}

export function formatSenderFrom(locationOrBusiness: Record<string, unknown> | null | undefined): string {
  return formatAddress(locationOrBusiness);
}

export function shipToEqualsSender(shipTo: string, sender: string): boolean {
  const a = shipTo.trim().toLowerCase();
  const b = sender.trim().toLowerCase();
  if (!a || !b || a === "—" || b === "—") return false;
  return a === b;
}

export function uniqueFulfilmentPrintOrderIds(ids: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    const v = (id ?? "").trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

export function visibleFulfilmentPrintOrderIds(opts: {
  tab: FulfilmentTab;
  pickingOrderIds: Array<string | null | undefined>;
  packingOrderIds: Array<string | null | undefined>;
  dispatchOrderIds: Array<string | null | undefined>;
}): string[] {
  if (opts.tab === "packing") return uniqueFulfilmentPrintOrderIds(opts.packingOrderIds);
  if (opts.tab === "dispatch") return uniqueFulfilmentPrintOrderIds(opts.dispatchOrderIds);
  if (opts.tab === "shipments") return [];
  return uniqueFulfilmentPrintOrderIds(opts.pickingOrderIds);
}
